import React, { useMemo, useState } from "react";

import { Grid } from "@material-ui/core";
import { Form, Formik, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { SaleAction } from "actions";

import { Button, SecondaryButton } from "components/Form";
import {
  CheckBox,
  Error,
  Input,
  LabelText,
  withNamespace,
} from "components/Form/FormikControls";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import {
  getDeployments,
  getIsOnline,
  getSaleById,
  selectAgencyByDeploymentIdLookup,
  selectBusinessUserLinkedAgentRole,
} from "selectors";
import { FormikContextType } from "formik/dist/types";
import { BoldText } from "components/Text";
import { Row } from "components/Layout";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DeploymentBusinessAutoCompletePicker } from "components/Form/AutoCompleteBusinessPicker";

function DeploymentsMapping({ isOnline }: { isOnline: boolean }) {
  const { values }: FormikContextType<FormValues> = useFormikContext();

  const {
    userRole,
    deploymentMapping,
    includeMySales,
    agentRoleDeploymentIds,
  } = values;
  const deploymentByIdLookup = useSelector(getDeployments);
  const agencyByDeploymentIdLookup =
    useSelector(selectAgencyByDeploymentIdLookup) || {};

  const deploymentIdsToMap = useMemo(() => {
    if (!includeMySales) {
      return Object.keys(deploymentMapping).filter(
        deploymentId =>
          !agentRoleDeploymentIds.includes(parseInt(deploymentId, 10)),
      );
    }

    return Object.keys(deploymentMapping);
  }, [agentRoleDeploymentIds, deploymentMapping, includeMySales]);

  return (
    <Grid container item>
      <Grid item xs={12}>
        <LabelText>
          Vendor Mapping{" "}
          <Tooltip title="This allows mapping of existing businesses as vendors for the created Ex Agent Sale. The dropdowns will allow selection of businesses available in your agent role. If a business is not available you will need to create one in your Agent Role. If this form is submitted without a mapping, we will attempt to find the agency vendor business, or create a new one for you." />
        </LabelText>
      </Grid>
      {deploymentIdsToMap.map(deploymentId => (
        <Row marginVertical={1} fullWidth key={deploymentId}>
          <Grid item xs={6} alignContent="center">
            <BoldText>
              {deploymentByIdLookup[deploymentId]?.name ||
                agencyByDeploymentIdLookup[deploymentId]?.name}
              :{" "}
            </BoldText>
          </Grid>
          <Grid item xs={6}>
            <DeploymentBusinessAutoCompletePicker
              name={withNamespace("deploymentMapping", deploymentId)}
              userRole={userRole}
              disabled={!isOnline}
            />
          </Grid>
        </Row>
      ))}
      {!isOnline && (
        <Error>Mapping Selection is only available if you are online.</Error>
      )}
    </Grid>
  );
}

interface CreateExternalAgentSaleFromSaleModalProps {
  onClose: () => void;
  saleId: number;
}

interface FormValues {
  saleTitle: string;
  deploymentMapping: Record<number, string>;
  userRole: string;
  includeMySales: boolean;
  agentRoleDeploymentIds: number[];
}

const validationSchema = Yup.object().shape({
  saleTitle: Yup.string().required("Sale Title is required"),
});

export const CreateExternalAgentSaleFromSaleModal = ({
  onClose,
  saleId,
}: CreateExternalAgentSaleFromSaleModalProps) => {
  const dispatch = useDispatch();

  const sale = useSelector(getSaleById(saleId));

  const isOnline = useSelector(getIsOnline);

  const [isMissingMappingDialogOpen, setIsMissingMappingDialogOpen] =
    useState(false);

  const {
    saleyard_name: saleyardName,
    deployment_sales: deploymentSales = [],
  } = sale || {};

  const { slug: agentRoleSlug, deployments: agentRoleDeployments } =
    useSelector(selectBusinessUserLinkedAgentRole) || {};
  const agentRoleDeploymentIds = agentRoleDeployments.map(
    deployment => deployment.id,
  );

  const initialSaleTitle = `Ex. Agt ${saleyardName}`;

  const initialValues: FormValues = {
    saleTitle: initialSaleTitle,
    userRole: agentRoleSlug,
    includeMySales: false,
    deploymentMapping: deploymentSales.reduce((acc, deploymentSale) => {
      acc[deploymentSale.deployment_id] = null;
      return acc;
    }, {}),
    agentRoleDeploymentIds,
  };

  const handleSubmit = (values: FormValues) => {
    const { saleTitle, deploymentMapping, includeMySales } = values;

    dispatch(
      SaleAction.createExternalAgentSaleFromSale(
        saleId,
        agentRoleSlug,
        saleTitle,
        deploymentMapping,
        includeMySales,
      ),
    );
    onClose();
  };

  const checkUnmappedDeployments = ({ includeMySales, deploymentMapping }) =>
    Object.entries(deploymentMapping).some(([deploymentId, mappedName]) => {
      if (!includeMySales) {
        if (agentRoleDeploymentIds.includes(parseInt(deploymentId, 10))) {
          return false;
        }
      }

      return !mappedName;
    });

  const showIncludeMySales = deploymentSales.find(deploymentSale =>
    agentRoleDeploymentIds.includes(deploymentSale.deployment_id),
  );

  return (
    <ZoomyDialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle onClose={onClose}>Create External Agent Sale</DialogTitle>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {({ values }) => (
          <>
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Input
                      label="Sale Title"
                      name="saleTitle"
                      type="text"
                      tooltip="This is the sale title to be set for the new External Agent Sale."
                    />
                  </Grid>

                  <DeploymentsMapping isOnline={isOnline} />
                  {showIncludeMySales && (
                    <Grid item xs={12}>
                      <CheckBox
                        name="includeMySales"
                        label="Include lots purchased from my agency."
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <SecondaryButton onClick={onClose}>Back</SecondaryButton>

                <Button
                  disabled={!isOnline}
                  onClick={
                    checkUnmappedDeployments(values)
                      ? () => setIsMissingMappingDialogOpen(true)
                      : () => handleSubmit(values)
                  }
                  type="button"
                >
                  Create
                </Button>
              </DialogActions>
            </Form>
            {isMissingMappingDialogOpen && (
              <ConfirmDialog
                title="Confirm you want to Create Sale with Missing Mapping"
                message="Some Vendors are missing a mapping. If you submit without a mapping, we will attempt to find the agency vendor business, or create a new one for you. This could result in duplicate Businesses."
                isOpen={isMissingMappingDialogOpen}
                onCancel={() => setIsMissingMappingDialogOpen(false)}
                onDelete={() => {
                  handleSubmit(values);
                }}
                buttonMessage="Create"
              />
            )}
          </>
        )}
      </Formik>
    </ZoomyDialog>
  );
};
